import { useStripe } from '@stripe/react-stripe-js';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { BsInfoCircle } from 'react-icons/bs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import NoProductsBanner from '@/components/banner/NoProductsBanner';
import MerchantAddModal from '@/components/modal/merchant/MerchantAddModal';
import SubscriptionConfirmationModal from '@/components/modal/merchant/SubscriptionConfirmationModal';
import { StripeConnectButton } from '@/components/stripe/StripeConnectButton';
import {
	CircleTickIcon,
	MerchantEditIcon,
	MerchantInfoIcon,
	PlanDetailIcon,
} from '@/components/ui/svg/DashboardIcons';
import LeftArrowIcon from '@/components/ui/svg/LeftArrowIcon';
import RightArrowIcon from '@/components/ui/svg/RightArrowIcon';
import {
	MAX_LIMIT,
	generateUpgradeMessage,
	getPlanStyle,
} from '@/components/utils/constants';
import { SubscriptionPlans } from '@/components/utils/enums';
import { useConfigPlans } from '@/hooks/useConfigPlans';
import { useModal } from '@/hooks/useModal';
import { useUpdateRoles, useUser } from '@/hooks/useUser';
import { useUserStore } from '@/hooks/useUserStore';
import { ApiQueryKeys } from '@/interfaces/api/ApiQueryKeys';
import { InventoryType } from '@/interfaces/api/InventoryType';
import { UserRole } from '@/interfaces/api/user/UserRole.enum';
import { AppRoutes } from '@/interfaces/routes/AppRoutes';
import { haggleApiService } from '@/services/haggle.service';
import { notificationService } from '@/services/notification.service';
import { subscriptionService } from '@/services/subscription.service';

const Dashboard = () => {
	const { plans } = useConfigPlans();
	const stripe = useStripe();
	const navigate = useNavigate();
	const { user } = useUser();
	const { store } = useUserStore({ withEvents: true });
	const userIsAdmin = user?.roles.includes('admin');
	const userPlan = user?.roles.find((role) => role.includes('merchant'));
	const [loading, setLoading] = useState(false);
	const [planData, setPlanData] = useState<any>({
		planName: SubscriptionPlans.emerald,
		planPrice: 0,
		endsOn: 'N/A',
		cancelled: false,
		nextPlan: null,
	});
	const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
	const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlans | null>(
		null,
	);
	const toastShownRef = useRef(false);
	const [, setSearchParams] = useSearchParams();

	const { modal, openModal, closeModal } = useModal({
		children: <MerchantAddModal defaultClose={() => closeModal()} />,
		closeOnOutClick: true,
		modalBoxClassName: 'w-full max-w-3xl',
	});

	const {
		data: chatCountData,
		isLoading: chatCountIsLoading,
		isError: chatCountIsError,
	} = useQuery<{ count: number }>({
		queryKey: [ApiQueryKeys.getMerchantHaggleChatCount],
		queryFn: () => haggleApiService.getMerchantHaggleChatCount(),
		refetchOnWindowFocus: false,
	});
	const [currentScroll, setCurrentScroll] = useState(0);
	const containerRef = useRef<HTMLDivElement>(null);

	//Uncomment to revert back to show Diamond plan contact modal
	// const {
	// 	modal: contactModal,
	// 	openModal: openContactModal,
	// 	closeModal: closeContactModal,
	// } = useModal({
	// 	children: (
	// 		<ContactForm
	// 			closeModal={() => {
	// 				closeContactModal();
	// 			}}
	// 		/>
	// 	),
	// 	closeOnOutClick: false,
	// 	modalBoxClassName: 'w-full max-w-xl',
	// });

	const updateRolesMutation = useUpdateRoles();

	const lastMutatedPlanDataRef = useRef<any>(null);

	useQuery({
		queryKey: [ApiQueryKeys.syncStoreFromShopify],
		queryFn: () => haggleApiService.syncStoreFromShopify(),
		enabled: user?.inventoryType === InventoryType.SHOPIFY && !userIsAdmin,
		refetchOnWindowFocus: false,
		retry: false,
	});

	useEffect(() => {
		if (userIsAdmin) {
			navigate(AppRoutes.AdminMerchants);
		} else {
			fetchUserPlanDetails();
			checkIfThemeNeedsToReset();
		}
	}, [store]);

	useEffect(() => {
		if (
			planData.planName &&
			(user || (store && store.shop)) &&
			JSON.stringify(planData) !==
				JSON.stringify(lastMutatedPlanDataRef.current)
		) {
			updateRolesMutation.mutate(
				{ user, store, planData },
				{
					onSuccess: (response) => {
						if (response && response.message) {
							window.location.href = AppRoutes.UserDashboard;
						}
					},
					onError: () => {
						setSearchParams({}, { replace: true });
					},
				},
			);
			lastMutatedPlanDataRef.current = planData;
		}
	}, [planData, user, store]);

	const fetchUserPlanDetails = async () => {
		try {
			const planDetails = await subscriptionService.fetchUserPlanDetails(
				user,
				store,
			);
			setPlanData(planDetails);
		} catch (error: Error | any) {
			setPlanData({
				planName: SubscriptionPlans.emerald,
				planPrice: 0,
				endsOn: 'N/A',
				cancelled: false,
				nextPlan: null,
			});
			if (error.message !== 'Error: No active subscription found') {
				notificationService.error(
					'Failed to fetch user plan details: ' + error.message,
				);
			}
		}
	};

	const selectCard = async (plan: SubscriptionPlans) => {
		setSelectedPlan(plan);
		setShowSubscriptionModal(true);
	};

	const cancelSubscription = () => {
		setShowSubscriptionModal(false);
		setSelectedPlan(null);
	};

	const confirmSubscription = async () => {
		setLoading(true);
		try {
			const response = await subscriptionService.manageSubscription(
				user,
				store,
				selectedPlan as string,
			);

			// Handle Stripe checkout session
			if (stripe && response && response.id) {
				const { error } = await stripe.redirectToCheckout({
					sessionId: response.id,
				});

				if (error) {
					notificationService.error('Failed to redirect to Stripe Checkout');
				}
			}
			// Handle subscription cancellation message
			else if (response && response.message) {
				notificationService.success(response.message);
				// Add delay before reload
				setTimeout(() => {
					window.location.reload();
				}, 2000);
			}
			// Handle Shopify subscription cancellation
			else if (response?.appSubscriptionCancel) {
				const { status } = response.appSubscriptionCancel.appSubscription;
				if (status === 'CANCELLED') {
					setPlanData({
						planName: SubscriptionPlans.emerald,
						planPrice: 0,
						endsOn: 'N/A',
						cancelled: false,
						nextPlan: null,
					});
					notificationService.success('Successfully unsubscribed from plan');
				}
			}
			// Handle Shopify confirmation URL
			else if (response?.confirmationUrl) {
				window.location.href = response.confirmationUrl;
			}
			// Handle failure case
			else {
				notificationService.error('Failed to manage subscription');
			}
		} catch (error: Error | any) {
			notificationService.error(error.message);
		} finally {
			setLoading(false);
			setShowSubscriptionModal(false);
			setSelectedPlan(null);
		}
	};

	useEffect(() => {
		if (userIsAdmin) {
			navigate(AppRoutes.AdminMerchants);
		}
	}, [userIsAdmin, navigate]);

	const accessBillingPortal = async () => {
		setLoading(true);
		try {
			const response = await subscriptionService.accessBillingPortal(
				user,
				store,
			);

			if (response && response.url) {
				window.location.href = response.url;
			} else {
				notificationService.error(
					'Either not already a customer or an error occurred',
				);
			}
		} catch (error) {
			notificationService.error(
				'Either not already a customer or an error occurred',
			);
		} finally {
			setLoading(false);
		}
	};

	const getLimitByRole = () => {
		const limit = plans.find((obj) => obj.role === userPlan)?.limit;

		return limit === MAX_LIMIT ? 'Unlimited' : limit;
	};

	const isAtLimit = () => {
		if (!chatCountData?.count || userPlan === UserRole.MerchantDiamond)
			return false;
		const limit = getLimitByRole();
		if (limit === undefined) return false;
		return chatCountData.count >= (typeof limit === 'number' ? limit : 0);
	};

	const extraHaggleCharges = () => {
		if (!chatCountData?.count || userPlan === UserRole.MerchantDiamond)
			return 0;
		const limit = getLimitByRole();
		if (limit === undefined) return 0;
		if (chatCountData.count > (typeof limit === 'number' ? limit : 0)) {
			return (
				(chatCountData.count - (typeof limit === 'number' ? limit : 0)) * 0.5
			);
		}
		return 0;
	};

	const extraHagglesCount = () => {
		if (!chatCountData?.count || userPlan === UserRole.MerchantDiamond)
			return 0;
		const limit = getLimitByRole();
		if (limit === undefined) return 0;
		return chatCountData.count - (typeof limit === 'number' ? limit : 0);
	};

	const isNearLimit = () => {
		if (!chatCountData?.count || userPlan === UserRole.MerchantDiamond)
			return false;
		const limit = getLimitByRole();
		if (limit === undefined) return false;
		return (
			chatCountData.count >= (typeof limit === 'number' ? limit * 0.9 : 0) &&
			chatCountData.count < (typeof limit === 'number' ? limit : 0)
		);
	};

	const checkIfThemeNeedsToReset = () => {
		if (planData.planName === SubscriptionPlans.emerald) {
			if (
				store?.theme ||
				store?.themeType ||
				store?.avatarUrl ||
				store?.businessLogoUrl
			) {
				resetTheme();
			}
		}
	};

	const { mutate: resetTheme } = useMutation({
		mutationFn: () => {
			if (!store?.id) throw new Error('Store not found');

			return haggleApiService.updateChatbotSettings(store.id, {
				resetTheme: true,
				resetLogo: true,
				resetAvatar: true,
			});
		},
		onError: (error: any) => {
			notificationService.error(
				error?.response?.data?.message || 'Update failed',
			);
		},
	});

	function getUpgradeMessageByPlanName(
		planName: string | undefined,
		isNearLimit = false,
	): string {
		const currentIndex = plans.findIndex((plan) => plan.role === planName);
		if (currentIndex === -1) return 'Invalid Plan Name';

		const currentPlan = plans[currentIndex];
		const nextPlan = currentIndex !== 0 ? plans[currentIndex - 1] : undefined;
		return generateUpgradeMessage({
			currentPlan,
			nextPlan,
			isNearLimit,
		});
	}

	useEffect(() => {
		if (isAtLimit() && !toastShownRef.current) {
			const upgradeMessage = getUpgradeMessageByPlanName(userPlan);

			toast.info(upgradeMessage);
			toastShownRef.current = true;
		} else if (isNearLimit()) {
			const upgradeMessageNearLimit = getUpgradeMessageByPlanName(
				userPlan,
				true,
			);

			toast.info(upgradeMessageNearLimit);
			toastShownRef.current = true;
		} else if (!isAtLimit() && !isNearLimit()) {
			toastShownRef.current = false;
		}
	}, [chatCountData?.count, userPlan]);

	return (
		<div className="flex flex-col h-full w-full">
			{/* Haggle Member Modal */}

			{showSubscriptionModal && (
				<SubscriptionConfirmationModal
					isOpen={showSubscriptionModal}
					isLoading={loading}
					onClose={cancelSubscription}
					onConfirm={confirmSubscription}
					title="Confirm Subscription"
					message={`Are you sure you want to subscribe to the ${selectedPlan} plan?`}
					confirmText="Subscribe"
					cancelText="Cancel"
				/>
			)}

			{!userIsAdmin && (
				<>
					<div className="h-[100%] bg-gray-70 p-6">
						<NoProductsBanner
							onGoToProducts={() => navigate('/user/dashboard/products')}
						/>
						{user?.inventoryType === InventoryType.CSV && (
							<div className="w-[100%] mb-6">
								<StripeConnectButton isSizeCompact={false} />
							</div>
						)}

						<div className="grid gap-6 mb-12 grid-cols-1 lg:grid-cols-2">
							<div className="bg-white rounded-lg shadow p-6">
								<div className="flex items-center justify-between mb-4">
									<div className="flex gap-3 items-center">
										<MerchantInfoIcon />
										<h2 className="text-xl font-semibold">
											Merchant Information
										</h2>
									</div>
									<button onClick={() => openModal()}>
										<MerchantEditIcon />
									</button>
									{modal}
								</div>
								<div className="space-y-3">
									<div className="flex justify-between">
										<span className="font-medium text-gray-600">
											Business Name
										</span>
										<span className="text-gray-800 truncate">
											{user?.businessName}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="font-medium text-gray-600">
											Email Address
										</span>
										<span className="text-gray-800 truncate">
											{user?.username}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="font-medium text-gray-600">
											Trade Name
										</span>
										<span className="text-gray-800 truncate">
											{store && store.tradeName && <>{store.tradeName}</>}
										</span>
									</div>
									<div className="flex justify-between">
										<span className="font-medium text-gray-600">Industry</span>
										<span className="text-gray-800 truncate">
											{user?.industry}
										</span>
									</div>

									<div className="flex justify-between">
										<span className="font-medium text-gray-600">
											Sub Industry
										</span>
										<span className="text-gray-800 truncate">
											{user?.subIndustry}
										</span>
									</div>
								</div>
							</div>

							<div className="bg-white rounded-lg shadow p-6">
								<div className="flex gap-3 items-center mb-4">
									<PlanDetailIcon />
									<h2 className="text-xl font-semibold">Plan Details</h2>
								</div>
								<div className="space-y-3">
									<div className="flex justify-between">
										<span className="font-medium text-gray-600">Plan Name</span>
										<span className="text-gray-800">{planData.planName}</span>
									</div>
									<div className="flex justify-between">
										<span className="font-medium text-gray-600">
											Plan Price
										</span>
										<span className="text-gray-800">${planData.planPrice}</span>
									</div>
									{planData.endsOn && planData.endsOn !== 'N/A' && (
										<div className="flex justify-between">
											<span className="font-medium text-gray-600">
												{planData.cancelled
													? 'Subscription ends on'
													: 'Subscription renews on'}
											</span>
											<span className="text-gray-800">
												{new Date(planData.endsOn).toLocaleDateString('en-GB', {
													day: '2-digit',
													month: 'long',
													year: 'numeric',
												})}
											</span>
										</div>
									)}
									{planData.nextPlan && (
										<div className="flex justify-between">
											<span className="font-medium text-gray-600">
												Upcoming Plan
											</span>
											<span className="text-gray-800">{planData.nextPlan}</span>
										</div>
									)}
									<div className="flex justify-between">
										<span className="font-medium text-gray-600">
											Monthly Negotiations usage
										</span>
										<div className="flex items-center gap-2">
											<span
												className={`${
													isAtLimit() || isNearLimit()
														? 'text-red-500 font-bold animate-pulse'
														: 'text-gray-800'
												}`}
											>
												{chatCountIsLoading
													? 'Loading...'
													: chatCountIsError
													? 'Error fetching count'
													: `${chatCountData?.count || 0}/${getLimitByRole()}`}
											</span>
											<span>
												<BsInfoCircle
													className="cursor-pointer"
													data-tooltip-id="haggle-limit-tooltip"
												/>
											</span>
											<ReactTooltip
												id="haggle-limit-tooltip"
												place="top-start"
												content={`Once the limit is reached, you will be
													charged 0.5$ per extra haggle that will
													be added to your next bill.`}
												style={{ whiteSpace: 'pre-line' }}
											/>
										</div>
									</div>
									{extraHaggleCharges() > 0 && (
										<div className="flex justify-between">
											<span className="font-medium text-gray-600">
												Extra Charges
											</span>
											<div className="flex items-center gap-2">
												<span className="text-red-500 font-bold">
													${extraHaggleCharges()}
												</span>
												<span>
													<BsInfoCircle
														className="cursor-pointer"
														data-tooltip-id="extra-haggle-charges-tooltip"
													/>
												</span>
												<ReactTooltip
													id="extra-haggle-charges-tooltip"
													place="top-start"
													content={`Extra haggle charges calculation:
														0.5$ * ${extraHagglesCount()} Extra Haggles`}
													style={{ whiteSpace: 'pre-line' }}
												/>
											</div>
										</div>
									)}
									<div className="flex justify-end">
										<button
											disabled={loading}
											className={`my-4 bg-purpleBrand-500 hover:bg-purpleBrand-600 rounded-md active:bg-purpleBrand-900 py-2 px-5 text-white transition
		${loading ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'}`}
											type="button"
											onClick={accessBillingPortal}
										>
											{loading ? 'Loading...' : 'Go to Billing Portal'}
										</button>
									</div>
								</div>
							</div>
						</div>

						<div className="border-2 rounded-full border-white my-8" />

						<div className="text-center my-10">
							<h2 className="text-2xl font-semibold mb-6">Payment Plan</h2>
						</div>
						<div className="relative mb-12">
							<div
								ref={containerRef}
								className="flex overflow-x-hidden scrollbar-hide snap-x snap-mandatory scroll-smooth gap-4 pb-4 p-2"
								onScroll={(e) => setCurrentScroll(e.currentTarget.scrollLeft)}
							>
								{plans.map((card) => {
									const { shadowColor, badgeColor, background } = getPlanStyle(
										card.planName,
									);
									return (
										<div
											key={card?.name}
											className={`min-w-[85%] sm:min-w-[45%] lg:min-w-0 lg:w-[24%] snap-center flex-shrink-0 bg-white rounded-lg p-6 flex flex-col ${
												userPlan === card.role
													? `transition-all duration-300 ${shadowColor}`
													: ''
											}`}
										>
											<div className="flex items-baseline justify-between mb-2">
												<div className={`rounded-full ${badgeColor} py-1 px-3`}>
													<p className="text-sm font-bold text-black uppercase">
														{card.name}
													</p>
												</div>
											</div>

											<div className="my-5 flex items-center">
												<div className="h-[100%]">
													<p className="text-4xl font-bold mr-3">
														{'$' + (card.price === 0 ? 'FREE' : card?.price)}
													</p>
												</div>
												{card.priceNote && (
													<p className="text-gray-600 text-xs mt-2">
														{card.priceNote}
													</p>
												)}
											</div>

											<button
												disabled={
													loading ||
													(!planData.cancelled && userPlan === card.role)
												}
												onClick={() => selectCard(card?.planName)}
												style={{ backgroundImage: `url(${background})` }}
												className={`bg-cover bg-center text-black font-bold px-4 py-2 rounded mb-4 transition ${
													loading ||
													(!planData.cancelled && userPlan === card.role)
														? 'opacity-50 cursor-not-allowed'
														: 'active:scale-95'
												}`}
											>
												{loading
													? 'Loading...'
													: planData.cancelled && userPlan === card.role
													? 'Re-subscribe'
													: userPlan === card.role
													? 'Subscribed'
													: 'Select'}
											</button>

											<hr />
											<ul className="space-y-2 text-gray-700 mt-5">
												{card?.features?.map((feature, i) => (
													<li key={i} className="flex gap-2 items-center">
														<CircleTickIcon /> {feature}
													</li>
												))}
											</ul>
										</div>
									);
								})}
							</div>

							<div className="pointer-events-none absolute inset-[-30px] flex items-center justify-between lg:hidden">
								<button
									onClick={() =>
										containerRef.current?.scrollBy({
											left: -containerRef.current.offsetWidth,
											behavior: 'smooth',
										})
									}
									disabled={currentScroll === 0}
									className="pointer-events-auto flex items-center justify-center transition-all disabled:opacity-0 disabled:pointer-events-none"
								>
									<LeftArrowIcon />
								</button>

								<button
									onClick={() =>
										containerRef.current?.scrollBy({
											left: containerRef.current.offsetWidth,
											behavior: 'smooth',
										})
									}
									disabled={
										currentScroll >=
										(containerRef.current?.scrollWidth ?? 0) -
											(containerRef.current?.offsetWidth ?? 0)
									}
									className="pointer-events-auto flex items-center justify-center transition-all disabled:opacity-0 disabled:pointer-events-none"
								>
									<RightArrowIcon />
								</button>
							</div>
						</div>
					</div>
				</>
			)}
			{/* {contactModal}  Uncomment to revert back to show Diamond plan contact modal*/}
		</div>
	);
};

export default Dashboard;
